import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { TableBody, TableHead, TableRow } from '@mui/material';
import { cancelInviteMemberAppLink, getMemberAppLinks, rejectMemberAppLink } from '../../api/memberAppLink';
import AnimationPage from '../../pages/AnimationPage';
import CommonSegmentedControl from '../../form-components/CommonSegmentedControl';
import CommonModalButton from '../../form-components/CommonModalButton';
import UnlinkConfirmModal from './UnlinkConfirmModal';
import CommonLoading from '../../form-components/CommonLoading';
import ApproveLinkModal from './ApproveLinkModal';
import RejectLinkModal from './RejectLinkModal';
import AppLinkQrModal from './AppLinkQrModal';
import AutoApproveControl from './AutoApproveControl';
import {
  AppLinkTable,
  BodyCell,
  CellLine,
  EmptyText,
  HeaderCell,
  ListContainer,
  ListHeader,
  ListTitle,
  RowActions,
  StatusBadge,
  AutoApprovedTag,
  TableWrapper,
} from './MemberAppLinkStyled';

const STATUS_PENDING = 'PENDING';
const STATUS_INVITED = 'INVITED';
const STATUS_CANCELED = 'CANCELED';

const TAB_OPTIONS = [
  { value: 'pending', label: '대기' },
  { value: 'invited', label: '초대 중' },
  { value: 'processed', label: '처리됨' },
];

const STATUS_VIEW = {
  PENDING: { label: '대기', color: '#F59E0B' },
  LINKED: { label: '연동됨', color: '#4AB3BC' },
  REJECTED: { label: '거절됨', color: '#9CA3AF' },
  WITHDRAWN: { label: '요청 철회', color: '#9CA3AF' },
  UNLINKED: { label: '연동 취소', color: '#6B7280' },
  INVITED: { label: '초대 중', color: '#3B82F6' },
  DECLINED: { label: '회원 거절', color: '#9CA3AF' },
  CANCELED: { label: '초대 취소', color: '#9CA3AF' },
};

const TARGET_TYPE_LABEL = { MEMBER: '회원', TEMPORARY_USER: '잠재회원' };

// 합칠 대상 없이 지점만 연동된 행(2026-10-20 지점 단위 앱 연동)
const NO_TARGET_LABEL = {
  APP_PURCHASE: '앱에서 구매해 자동 연동',
  BACKFILL: '기존 이용 회원 일괄 연동',
  QR_SCAN: '센터 QR로 연동',
};

// 초대 중은 초대일, 초대 취소는 취소일을 보여준다.
const processedDay = link => {
  if (link.status === STATUS_INVITED) return link.registeredDate;
  if (link.status === STATUS_CANCELED) return link.unlinkedDate;
  return link.processedDate;
};

const toDay = value => (value ? String(value).slice(0, 10) : '-');

const MemberAppLinkList = () => {
  const user = useSelector(state => state.user.user);
  const branchId = user.branch?.branchId;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('pending');
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [cancelInviteTarget, setCancelInviteTarget] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  // 처리됨 탭은 상태가 여러 개라 전체를 받아 대기 건을 걸러낸다.
  const { data: links = [], isLoading } = useQuery({
    queryKey: ['memberAppLinks', branchId],
    queryFn: () => getMemberAppLinks(branchId),
    enabled: !!branchId,
  });

  const rejectMutation = useMutation({
    mutationFn: link => rejectMemberAppLink(link.id),
    onSuccess: (_, link) => {
      toast.success('요청을 거절했습니다.');
      queryClient.invalidateQueries({ queryKey: ['memberAppLinks'] });
      queryClient.invalidateQueries({ queryKey: ['memberAppLinkPendingCount'] });
      // 그 사이 다른 행의 창을 열었다면 닫지 않는다
      setRejectTarget(current => (current?.id === link.id ? null : current));
    },
  });

  // 더블클릭이 리렌더 전에 두 번 들어오는 것을 막는다(isPending 은 리렌더 후에야 반영된다).
  const isRejecting = useRef(false);
  const runRejectMutation = () => {
    if (isRejecting.current) return;
    isRejecting.current = true;
    rejectMutation.mutate(rejectTarget, { onSettled: () => (isRejecting.current = false) });
  };

  const cancelInviteMutation = useMutation({
    mutationFn: () => cancelInviteMemberAppLink(cancelInviteTarget.id),
    onSuccess: () => {
      toast.success('초대를 취소했습니다.');
      queryClient.invalidateQueries({ queryKey: ['memberAppLinks'] });
      queryClient.invalidateQueries({ queryKey: ['memberAppLinkHistory'] });
      setCancelInviteTarget(null);
    },
  });

  const isCancelingInvite = useRef(false);
  const runCancelInviteMutation = () => {
    if (isCancelingInvite.current) return;
    isCancelingInvite.current = true;
    cancelInviteMutation.mutate(undefined, { onSettled: () => (isCancelingInvite.current = false) });
  };

  const isPendingTab = tab === 'pending';
  const isInvitedTab = tab === 'invited';
  const hasActions = isPendingTab || isInvitedTab;
  const rows = links.filter(link => {
    if (isPendingTab) return link.status === STATUS_PENDING;
    if (isInvitedTab) return link.status === STATUS_INVITED;
    return link.status !== STATUS_PENDING && link.status !== STATUS_INVITED;
  });
  const emptyText = isPendingTab
    ? '대기 중인 요청이 없습니다.'
    : isInvitedTab
      ? '초대 중인 건이 없습니다.'
      : '처리된 요청이 없습니다.';

  return (
    <AnimationPage>
      <ListContainer>
        <ListHeader>
          <ListTitle sx={{ marginBottom: 0 }}>앱 연동 요청</ListTitle>
          <RowActions>
            <AutoApproveControl branchId={branchId} />
            <CommonModalButton style={{ height: '40px' }} onClick={() => setQrOpen(true)}>
              앱 연동 QR
            </CommonModalButton>
          </RowActions>
        </ListHeader>
        <CommonSegmentedControl options={TAB_OPTIONS} value={tab} onChange={setTab} style={{ maxWidth: '280px' }} />
        <TableWrapper>
          {isLoading ? (
            <CommonLoading />
          ) : rows.length === 0 ? (
            <EmptyText>{emptyText}</EmptyText>
          ) : (
            <AppLinkTable>
              <colgroup>
                <col style={{ width: '110px' }} />
                <col style={{ width: '260px' }} />
                <col style={{ width: '110px' }} />
                <col />
                <col style={{ width: '110px' }} />
                {hasActions && <col style={{ width: '200px' }} />}
              </colgroup>
              <TableHead>
                <TableRow>
                  <HeaderCell>요청일</HeaderCell>
                  <HeaderCell>앱 계정</HeaderCell>
                  <HeaderCell>상태</HeaderCell>
                  <HeaderCell>연결 대상</HeaderCell>
                  <HeaderCell>처리일</HeaderCell>
                  {hasActions && <HeaderCell align="center">처리</HeaderCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(link => {
                  const statusView = STATUS_VIEW[link.status] ?? { label: link.status, color: '#9CA3AF' };
                  const accountLabel = `${link.appAccount?.name ?? ''} (${link.appAccount?.username ?? ''})`;
                  const targetLabel = link.targetName
                    ? `${link.targetName} (${TARGET_TYPE_LABEL[link.targetType] ?? ''})`
                    : NO_TARGET_LABEL[link.requestType] ?? '-';
                  return (
                    <TableRow key={link.id}>
                      <BodyCell>{toDay(link.registeredDate)}</BodyCell>
                      <BodyCell>
                        <CellLine title={accountLabel}>{accountLabel}</CellLine>
                        <CellLine>{link.appAccount?.phoneNumber || '-'}</CellLine>
                      </BodyCell>
                      <BodyCell>
                        <StatusBadge statusColor={statusView.color}>{statusView.label}</StatusBadge>
                        {link.autoApproved && <AutoApprovedTag>자동 승인</AutoApprovedTag>}
                      </BodyCell>
                      <BodyCell title={targetLabel}>{targetLabel}</BodyCell>
                      <BodyCell>{toDay(processedDay(link))}</BodyCell>
                      {isInvitedTab && (
                        <BodyCell align="center">
                          <RowActions>
                            <CommonModalButton
                              variant="cancel"
                              style={{ minWidth: '70px', height: '36px', padding: '0 16px' }}
                              onClick={() => setCancelInviteTarget(link)}>
                              초대 취소
                            </CommonModalButton>
                          </RowActions>
                        </BodyCell>
                      )}
                      {isPendingTab && (
                        <BodyCell align="center">
                          <RowActions>
                            <CommonModalButton style={{ minWidth: '70px', height: '36px', padding: '0 16px' }} onClick={() => setApproveTarget(link)}>
                              승인
                            </CommonModalButton>
                            <CommonModalButton
                              variant="cancel"
                              style={{ minWidth: '70px', height: '36px', padding: '0 16px' }}
                              onClick={() => setRejectTarget(link)}>
                              거절
                            </CommonModalButton>
                          </RowActions>
                        </BodyCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </AppLinkTable>
          )}
        </TableWrapper>
      </ListContainer>

      <AppLinkQrModal open={qrOpen} onClose={() => setQrOpen(false)} branchId={branchId} />
      <ApproveLinkModal open={!!approveTarget} onClose={() => setApproveTarget(null)} link={approveTarget} />
      <RejectLinkModal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={runRejectMutation}
        link={rejectTarget}
        isLoading={rejectMutation.isPending}
      />
      <UnlinkConfirmModal
        mode="cancelInvite"
        open={!!cancelInviteTarget}
        onClose={() => setCancelInviteTarget(null)}
        onConfirm={runCancelInviteMutation}
        isMember={cancelInviteTarget?.targetType === 'MEMBER'}
        account={{
          name: cancelInviteTarget?.targetName || cancelInviteTarget?.appAccount?.name,
          username: cancelInviteTarget?.appAccount?.username,
          date: toDay(cancelInviteTarget?.registeredDate),
        }}
        isLoading={cancelInviteMutation.isPending}
      />
    </AnimationPage>
  );
};

export default MemberAppLinkList;
